/**
 * Temporary entry point (Phase B1) — replaced by the Level-1 nav tab bar in
 * the later nav-refactor prompt.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { REGIONS } from '@olimhub/shared'
import type { Region, GeocodedPoint } from '@olimhub/shared'
import { groupsApi } from '@/api'
import { useToast } from '@/components/common/Toast'
import { useCategoryOptions, resolveActiveLanguage } from '@/hooks'
import { AddressAutocompleteInput, SectionCard } from '@/components/common'

export function CreateGroupPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isOpen, setIsOpen] = useState(true)
  const [region, setRegion] = useState<Region | ''>('')
  const [locationText, setLocationText] = useState('')
  // Set when the user picks a Google-resolved suggestion — lets submit skip
  // enqueueing the async geocode job. Cleared automatically if locationText
  // is edited after picking (AddressAutocompleteInput's stale-pick guard).
  const [resolvedPoint, setResolvedPoint] = useState<GeocodedPoint | null>(null)
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const { options: categoryOptions, language: categoryLang } = useCategoryOptions('group')

  async function handleSubmit() {
    if (!name.trim() || submitting) return
    setSubmitting(true)
    try {
      const group = await groupsApi.create({
        name: name.trim(),
        description: description.trim() || undefined,
        isOpen,
        region: region || undefined,
        locationText: locationText.trim() || undefined,
        ...(resolvedPoint && { latitude: resolvedPoint.latitude, longitude: resolvedPoint.longitude }),
        categoryId: categoryId || undefined,
      }, resolveActiveLanguage(i18n.language))
      navigate(`/groups/${group.slug}`)
    } catch {
      toast.error(t('groups.create_failed'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ padding: 16, maxWidth: 480, margin: '0 auto' }}>
      <h1 style={{ fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 'var(--fs-lg)', color: 'var(--text-strong)', marginBottom: 16 }}>
        {t('groups.create_group')}
      </h1>

      <SectionCard title={t('groups.section_basic_info')}>
        <label style={labelStyle}>{t('groups.name_label')}</label>
        <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} maxLength={64} />

        <label style={labelStyle}>{t('groups.description_label')}</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} maxLength={500} />

        <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={isOpen} onChange={(e) => setIsOpen(e.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--brand)' }} />
          {t('groups.is_open_label')}
        </label>

        <label style={labelStyle}>{t('post.category')} ({t('events.optional')})</label>
        <select value={categoryId ?? ''} onChange={(e) => setCategoryId(e.target.value || null)} style={inputStyle}>
          <option value="">{t('post.category_none')}</option>
          {categoryOptions.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.translations[categoryLang] ?? cat.translations.en}
            </option>
          ))}
        </select>
      </SectionCard>

      <SectionCard title={t('groups.section_location')}>
        <label style={labelStyle}>{t('groups.region_label')}</label>
        <select value={region} onChange={(e) => setRegion(e.target.value as Region | '')} style={inputStyle}>
          <option value="">{t('groups.region_none')}</option>
          {REGIONS.map((r) => (
            <option key={r} value={r}>{t(`groups.region.${r}`)}</option>
          ))}
        </select>

        <label style={labelStyle}>{t('groups.location_label')}</label>
        <AddressAutocompleteInput
          value={locationText}
          onChange={setLocationText}
          onSelect={(_, resolved) => setResolvedPoint(resolved ?? null)}
          placeholder={t('groups.location_placeholder')}
          style={inputStyle}
          maxLength={200}
        />
        <p style={{ margin: '4px 0 0', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-faint)' }}>
          {t('groups.location_hint')}
        </p>
      </SectionCard>

      <button
        type="button"
        disabled={!name.trim() || submitting}
        onClick={() => void handleSubmit()}
        style={{
          marginTop: 4,
          width: '100%',
          padding: '11px 0',
          borderRadius: 'var(--radius-md)',
          border: 'none',
          background: 'var(--brand)',
          color: 'var(--on-brand)',
          fontFamily: 'var(--font-sans)',
          fontWeight: 700,
          fontSize: 'var(--fs-body)',
          cursor: name.trim() && !submitting ? 'pointer' : 'not-allowed',
          opacity: name.trim() ? 1 : 0.55,
        }}
      >
        {t('groups.create_group')}
      </button>
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginTop: 14,
  marginBottom: 6,
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--fs-xs)',
  fontWeight: 600,
  color: 'var(--text-muted)',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: 'var(--radius-md)',
  border: '1.5px solid var(--border)',
  background: 'var(--bg-sunk)',
  color: 'var(--text-strong)',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--fs-body)',
  boxSizing: 'border-box',
  outline: 'none',
}
